/**
 * Gibbs Med-Chem Advice Shortcut.
 *
 * Recognizes design-intent queries and returns grounded rule-based advice.
 * Examples Gibbs can now answer deterministically:
 *   - "what isostere can replace this carboxylic acid?"
 *   - "where should I add fluorine to block metabolism?"
 *   - "is this compound a toxicophore?"
 *   - "how do I avoid the aniline alert?"
 *
 * This is a PRE-LLM shortcut — it surfaces rules with citations before (or
 * alongside) the LLM call, matching the existing shortcut chain in
 * gibbs/knowledge.
 *
 * @module gibbs/knowledge/medchemAdviceShortcut
 */

import {
    findRelevantRules,
    getRulesByCategory,
    formatRuleShort,
} from './medchemRules';

/**
 * Scan-action intent patterns. Matches user requests to EXECUTE a structural
 * alert scan on the active compound — distinct from asking about toxicophores
 * in general (which is handled by the advice intent below).
 *
 * IMPORTANT: check scan intent BEFORE advice intent in the shortcut chain,
 * otherwise "scan this compound for toxicophores" gets intercepted as an
 * advice query and never fires the actual scan.
 */
const SCAN_INTENT_PATTERNS = [
    /\bscan\b.*(toxic|alert|compound|structur)/i,
    /\b(check|screen)\b.*(toxic|alerts?|structur\w*\s+alerts?|reactive\s+metabolite)\b/i,
    /\b(run|perform)\b.*(structur\w*\s+alerts?|toxicophore\s+scans?|alert\s+scans?)\b/i,
    /\b(does|do|is)\b.*(compound|this|it)\b.*(have|contain).*(toxic|alert)/i,
    /\btoxicophore\w*.*(scan|check|filter|screen)\b/i,
    /\balerts?\s+scan\b/i,
    /\bflag.*toxicophore/i,
];

/**
 * Does the user's text look like a scan REQUEST (wants to execute the scan)
 * rather than a scan QUESTION (wants advice on toxicophores)?
 *
 * @param {string} text
 * @returns {boolean}
 */
export function matchScanAlertsIntent(text) {
    if (!text) return false;
    return SCAN_INTENT_PATTERNS.some((p) => p.test(text));
}

// Pattern order matters: more specific patterns must precede broader
// ones. Specifically, s-interaction patterns (which include "thiophene
// interaction") MUST scan before the toxicophore ring keyword list
// (which would otherwise hijack any "thiophene" mention as a
// toxicophore review). See gap #2 in the wording-robustness audit.
const INTENT_PATTERNS = [
    // Isostere queries — verb-form ("replace X with isostere", "isostere
    // for the cooh") or bare-noun ("amide bioisostere", "phenyl isostere
    // options"). The bare-noun pattern is required to catch the §18.1
    // smoke-test corpus exemplar "amide bioisostere".
    {
        pattern: /\b(isostere|bioisostere)s?\b.*\b(for|of|to replace|replace)\b/i,
        category: 'isostere',
        label: 'isostere suggestion',
    },
    {
        pattern: /\b(replace|swap|substitute)\b.*\b(carboxy|cooh|acid|amide|ester|phenyl|pyridine|ring)\b/i,
        category: 'isostere',
        label: 'isostere suggestion',
    },
    {
        // Bare-noun isostere phrasings: "amide bioisostere", "carboxy
        // isostere options", "phenyl bioisostere replacement". The
        // chemical noun must precede the (bio)isostere token; that
        // ordering keeps generic "isostere" mentions from hijacking
        // the more specific patterns below.
        pattern: /\b(carboxy|cooh|acid|amide|ester|phenyl|pyridine|ring|amine|aniline)\s+(bio)?isostere/i,
        category: 'isostere',
        label: 'isostere suggestion',
    },

    // Fluorine queries
    {
        pattern: /\b(add|place|put|use)\s+fluorine\b/i,
        category: 'fluorine',
        label: 'fluorine placement',
    },
    {
        pattern: /\b(block|prevent|stop).*(cyp|metabol|oxidation|clearance)\b/i,
        category: 'fluorine',
        label: 'fluorine for metabolism',
    },
    {
        pattern: /\b(lower|reduce|modulate)\s+pka\b/i,
        category: 'fluorine',
        label: 'fluorine for pKa modulation',
    },

    // Sulfur interaction queries — MUST precede the toxicophore patterns
    // below so phrases like "thiophene interaction with the protein"
    // route to s-interaction rather than being swallowed by the
    // toxicophore ring-keyword list.
    {
        pattern: /\b(sulfur|σ-hole|sigma hole|s-hole|thiophene.*interaction|o to s|n to s)\b/i,
        category: 's-interaction',
        label: 's-interaction design',
    },

    // Toxicophore queries
    {
        pattern: /\b(toxic|toxicophore|structural alert|reactive metabolite|dili|liver tox)\b/i,
        category: 'toxicophore',
        label: 'toxicophore review',
    },
    {
        pattern: /\b(aniline|furan|thiophene|hydrazine|nitroaromatic|thiazolidinedione|2-aminothiazole|quinone)\b/i,
        category: 'toxicophore',
        label: 'specific toxicophore mitigation',
    },

    // Heteroaromatic queries
    {
        pattern: /\b(which|what|pick|choose|compare)\b.*(ring|heteroaromatic|heterocycle|aromatic)\b/i,
        category: 'heteroaromatic',
        label: 'heteroaromatic ring choice',
    },

    // Interaction-tuning queries — "how do I improve/strengthen/tune the
    // [pi-pi | h-bond | halogen bond | salt bridge | cation-π] interaction".
    // Surfaced as Theme G during the 2026-05-14 Bill Kinney walkthrough:
    // chemist sees a pi-pi contact and asks how to make it stronger.
    {
        pattern: /\b(improve|strengthen|tune|make.*(stronger|better))\b.*\b(pi[-\s]?pi|pi[-\s]?stack|aromatic\s+stack|h[-\s]?bond|hydrogen\s+bond|halogen\s+bond|salt\s+bridge|cation[-\s]?pi|cation[-\s]?π|interaction)\b/i,
        category: 'interaction-tuning',
        label: 'interaction tuning',
    },
    {
        pattern: /\b(donor[\s-]+acceptor|electron[\s-]+(rich|poor|donating|withdrawing)|complementarity)\b.*\b(pair|pi|stack|ring|interaction)\b/i,
        category: 'interaction-tuning',
        label: 'interaction tuning',
    },
    {
        pattern: /\b(how|what).*\b(strengthen|stronger|tune|improve)\b.*\b(contact|interaction|stack|bond|bridge)\b/i,
        category: 'interaction-tuning',
        label: 'interaction tuning',
    },
    // Verb-after-noun phrasings: "make the H-bond stronger", "make this
    // pi-pi tighter", "make the halogen bond better".
    {
        pattern: /\b(make|get).*\b(pi[-\s]?pi|pi[-\s]?stack|aromatic\s+stack|h[-\s]?bond|hydrogen\s+bond|halogen\s+bond|salt\s+bridge|cation[-\s]?pi|cation[-\s]?π|interaction|contact|bond|bridge|stack)\b.*\b(stronger|better|tighter|deeper)\b/i,
        category: 'interaction-tuning',
        label: 'interaction tuning',
    },
];

/**
 * Does the user's text look like a medchem-intuition query?
 * @param {string} text
 * @returns {null | { category: string, label: string }}
 */
export function matchMedchemAdviceIntent(text) {
    if (!text) return null;
    for (const { pattern, category, label } of INTENT_PATTERNS) {
        if (pattern.test(text)) return { category, label };
    }
    return null;
}

/**
 * Build the shortcut response: a header, up to 5 relevant rules, and a
 * note that the user can ask for more detail.
 *
 * @param {string} userText
 * @param {{ category?: string, label?: string }} [hint] result of matchMedchemAdviceIntent
 * @returns {{ intent: string, rules: Array, summary: string } | null}
 */
export function buildMedchemAdvice(userText, hint) {
    const match = hint || matchMedchemAdviceIntent(userText);
    if (!match) return null;

    // Prefer keyword match over category-wide dump
    let rules = findRelevantRules(userText, 5);
    if (rules.length === 0 && match.category) {
        rules = getRulesByCategory(match.category).slice(0, 5);
    }
    if (rules.length === 0) return null;

    const header = headerFor(match);
    // Render each rule as its own markdown paragraph (blank line between
    // rules), so marked.parse() in ChatBot.jsx renders them as separate
    // visual blocks rather than one long wall of text. Surfaced as
    // Theme J during the 2026-05-14 Bill Kinney walkthrough.
    const body = rules.map((r, i) => `${i + 1}. ${formatRuleShort(r)}`).join('\n\n');
    const cites = Array.from(
        new Set(rules.flatMap((r) => r.references || []))
    ).map((c) => `- ${c}`).join('\n');

    const summary = [
        `### ${header}`,
        '',
        body,
        '',
        '**References:**',
        '',
        cites,
        '',
        '_Ask me to elaborate on any of these, or to apply one to a specific compound._',
    ].join('\n');

    return {
        intent: match.label,
        rules,
        summary,
    };
}

/**
 * Composable: try to run the medchem advice shortcut. If intent matches,
 * return { handled: true, response: <summary> }. Else null.
 *
 * Designed to plug into the existing shortcut chain in ChatBot.jsx / gibbs
 * pipeline, just like tryDockAndScoreShortcut etc.
 *
 * @param {string} userText
 * @returns {{ handled: boolean, response?: string, rules?: Array, intent?: string } | null}
 */
export function tryMedchemAdviceShortcut(userText) {
    const advice = buildMedchemAdvice(userText);
    if (!advice) return null;
    return {
        handled: true,
        response: advice.summary,
        rules: advice.rules,
        intent: advice.intent,
    };
}

function headerFor(match) {
    switch (match.category) {
        case 'isostere':
            return '**Isostere options (Meanwell 2011, J. Med. Chem.):**';
        case 'fluorine':
            return '**Fluorine strategies (Gillis & Meanwell 2015, J. Med. Chem.):**';
        case 'heteroaromatic':
            return '**Heteroaromatic ring guidance (Meanwell 2017, Adv. Het. Chem.):**';
        case 'toxicophore':
            return '**Toxicophore mitigation (Meanwell 2023 + Stepan/Kalgutkar 2011):**';
        case 's-interaction':
            return '**Sulfur non-covalent interactions (Meanwell 2023):**';
        case 'interaction-tuning':
            return '**Interaction-tuning guidance (Meanwell 2011 / 2017):**';
        default:
            return '**Med-chem intuition:**';
    }
}

export default {
    matchMedchemAdviceIntent,
    buildMedchemAdvice,
    tryMedchemAdviceShortcut,
    matchScanAlertsIntent,
};
